import React, { useState, useRef, useEffect, useMemo } from 'react';
import { LayoutList, BookOpen, MessageSquare } from 'lucide-react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';
import type { TranscriptEntry, WorkshopPhase } from '../../../types';
import type { SessionTaskSummary } from '../../../types/session-task.types';

type FocusTab = 'tasks' | 'transcript' | 'activities';

const TAB_ITEMS: { key: FocusTab; label: string; icon: React.ElementType }[] = [
  { key: 'tasks', label: 'Tasks', icon: LayoutList },
  { key: 'transcript', label: 'Transcript', icon: MessageSquare },
  { key: 'activities', label: 'Activities', icon: BookOpen },
];

const TASK_STATUS_BADGE_CLASSES: Record<string, string> = {
  completed: 'bg-green-500/20 text-green-400',
  running: 'bg-primary/20 text-primary',
};

interface TabContentProps {
  recentTranscript: TranscriptEntry[];
  getAvatarColor: (role?: string) => string;
  getAvatarName: (role?: string) => string;
  mutedTextColor: string;
  isDark: boolean;
  transcriptEndRef: React.RefObject<HTMLDivElement | null>;
  activeTasks: SessionTaskSummary[];
  phases: WorkshopPhase[];
  currentPhaseIndex: number;
}

const TranscriptTab: React.FC<TabContentProps> = ({
  recentTranscript,
  getAvatarColor,
  getAvatarName,
  mutedTextColor,
  isDark,
  transcriptEndRef,
}) => (
  <div className="flex flex-col gap-2">
    {recentTranscript.map((entry, i) => {
      const speakerColor = entry.speaker === 'avatar' ? getAvatarColor(entry.avatarRole) : mutedTextColor;
      return (
        <div key={entry.id ?? i} className="flex gap-2 items-start">
          <span
            className="text-xs font-bold uppercase shrink-0 w-20 truncate text-right"
            style={{ color: speakerColor }}
          >
            {entry.speaker === 'avatar' ? getAvatarName(entry.avatarRole) : 'Facilitator'}
          </span>
          <p className={`text-xs leading-relaxed ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
            {entry.text}
          </p>
        </div>
      );
    })}
    <div ref={transcriptEndRef} />
  </div>
);

const TasksTab: React.FC<TabContentProps> = ({ activeTasks, isDark }) => (
  <div className="flex flex-col gap-2">
    {activeTasks.length === 0 ? (
      <div className={`text-sm text-center py-6 ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        No active tasks
      </div>
    ) : (
      activeTasks.map((task) => (
        <div
          key={task.workflowId || `${task.taskName}-${task.taskCategory}`}
          className={`p-3 rounded-lg border ${
            isDark ? 'bg-white/[0.03] border-white/5' : 'bg-slate-50 border-slate-100'
          }`}
        >
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold uppercase px-1.5 py-0.5 rounded ${
              TASK_STATUS_BADGE_CLASSES[task.status] ?? 'bg-white/10 text-white/40'
            }`}>
              {task.status}
            </span>
            <span className={`text-xs font-semibold ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
              {task.taskName}
            </span>
          </div>
          <span className={`text-xs mt-1 block ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
            {task.taskCategory}
          </span>
        </div>
      ))
    )}
  </div>
);

const ActivitiesTab: React.FC<TabContentProps> = ({ phases, currentPhaseIndex, isDark }) => (
  <div className="flex flex-col gap-2">
    {phases.map((phase, i) => {
      const isCurrentPhase = i === currentPhaseIndex;
      let phaseClasses: string;
      if (isCurrentPhase) phaseClasses = isDark ? 'bg-primary/10 border-primary/30' : 'bg-primary/5 border-primary/20';
      else phaseClasses = isDark ? 'bg-white/[0.03] border-white/5' : 'bg-slate-50 border-slate-100';
      return (
        <div
          key={phase.id}
          className={`p-3 rounded-lg border ${phaseClasses}`}
        >
          <div className="flex items-center gap-2">
            {isCurrentPhase && (
              <div className="w-2 h-2 rounded-full bg-primary animate-pulse shrink-0" />
            )}
            <span className={`text-xs font-semibold ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
              {phase.name}
            </span>
            {phase.duration && (
              <span className={`text-xs ml-auto ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
                {phase.duration}
              </span>
            )}
          </div>
          {phase.objectives && (
            <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
              {phase.objectives}
            </p>
          )}
        </div>
      );
    })}
  </div>
);

const TAB_COMPONENTS: Record<FocusTab, React.FC<TabContentProps>> = {
  transcript: TranscriptTab,
  tasks: TasksTab,
  activities: ActivitiesTab,
};

const FocusViewWidget: React.FC<WidgetProps> = ({ config }) => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';
  const [activeTab, setActiveTab] = useState<FocusTab>(
    config?.defaultTab in TAB_COMPONENTS ? config.defaultTab : 'transcript'
  );
  const transcriptEndRef = useRef<HTMLDivElement>(null);

  const currentPhaseIndex = state?.currentPhaseIndex ?? 0;

  // Auto-scroll transcript to bottom
  useEffect(() => {
    if (activeTab === 'transcript' && transcriptEndRef.current) {
      transcriptEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [state?.transcript?.length, activeTab]);

  // Recent transcript entries (last 50) — committed only, no exceptions. This is
  // the one surface where showing the system's working (provisional fragments,
  // held, noise) is actively harmful in front of a client.
  const recentTranscript = useMemo(() => {
    const transcript = state?.transcript ?? [];
    const committedOnly = transcript.filter(entry => !entry.state || entry.state === 'committed');
    return committedOnly.slice(-50);
  }, [state?.transcript]);

  if (!state) return null;

  const sessionAvatars = state.sessionAvatars ?? [];
  const activeTasks = state.activeTasks ?? [];
  const phases = state.phases ?? [];

  const getAvatarColor = (role?: string) => {
    const avatar = sessionAvatars.find(a => a.role === role);
    return avatar?.primaryColor || '#00c7c4';
  };

  const getAvatarName = (role?: string) => {
    if (!role) return 'Facilitator';
    const avatar = sessionAvatars.find(a => a.role === role);
    return avatar?.name || role;
  };

  const mutedTextColor = isDark ? '#94a3b8' : '#64748b';

  const ActiveTabContent = TAB_COMPONENTS[activeTab];

  return (
    <div className="flex flex-col h-full">
      {/* Tab bar */}
      <div className={`flex shrink-0 border-b ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
        {TAB_ITEMS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          let tabClasses: string;
          if (isActive) tabClasses = 'text-primary border-b-2 border-primary';
          else if (isDark) tabClasses = 'text-white/40 hover:text-white/60';
          else tabClasses = 'text-slate-400 hover:text-slate-600';
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${tabClasses}`}
            >
              <Icon size={12} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content area */}
      <div className="flex-1 min-h-0 overflow-y-auto p-3">
        <ActiveTabContent
          recentTranscript={recentTranscript}
          getAvatarColor={getAvatarColor}
          getAvatarName={getAvatarName}
          mutedTextColor={mutedTextColor}
          isDark={isDark}
          transcriptEndRef={transcriptEndRef}
          activeTasks={activeTasks}
          phases={phases}
          currentPhaseIndex={currentPhaseIndex}
        />
      </div>
    </div>
  );
};

export default FocusViewWidget;
